import {
  Button,
  ConfigProvider,
  ConfirmModal,
  Flex,
  Table,
  type TableRef,
  TableRowMenuCell,
  Text,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useProject } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import { paymentValueConverter } from '@/features/project/_utils/payment';
import type {
  PaymentModel,
  PaymentsModel,
} from '@/features/project/projects/_models/payment';
import type { PaymentContractModel } from '@/features/project/projects/_models/payment-contract';
import { useDeletePaymentContract } from '@/features/project/projects/detail/payment/_hooks/payment-contract';
import { useId } from '@/hooks';
import { useLocaledAmount, useMessage } from '@/providers';
import { TABLE_MONETARY_AMOUNT_WIDTH, TABLE_ROW_MENU_WIDTH } from '@/utils';
import classNames from 'classnames';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import { PaymentContractsTable } from './PaymentContractsTable';
import styles from './PaymentsTable.module.scss';

type Props = {
  data: z.infer<typeof PaymentsModel>;
  paymentGroupId: string;
  onClickDelete: (value: z.infer<typeof PaymentModel>) => void;
};

const tableStyles = classNames(styles.ant_table_content);

const MINIMUM_ROW_NUM_TO_SCROLL = 5;
const PREVIEW_ROW: z.infer<typeof PaymentModel> = {
  id: PREVIEW_ROW_ID,
  createdAt: '',
  updatedAt: '',
  type: 'payee',
  name: '',
  isOthers: false,
  corporateNumber: null,
  corporateAddress: null,
  corporateKind: null,
  contracts: [],
  totalContractAmount: 0,
  paymentGroupId: '',
};

export const PaymentsTable: FC<Props> = ({
  data,
  paymentGroupId,
  onClickDelete,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { openMessage } = useMessage();
  const { setDrawerState } = useProjectDetailDrawerState();
  const tableRef = useRef<TableRef>(null);
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const [paymentContractForDelete, setPaymentContractForDelete] =
    useState<z.infer<typeof PaymentContractModel> | null>(null);
  const { mutateAsync: deletePaymentContractAsync } =
    useDeletePaymentContract();
  const editPermission = useProjectPermission(true);
  const { expandedRowKeys, onExpand } = useExpandedRowKeysContext();
  const { previewRowState, setPreviewRowState } = usePreviewRowState();
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();
  const { devtoolState } = useDevToolsStorage();

  const showPreviewRow = useMemo(
    () =>
      previewRowState?.type === 'payee-payment' &&
      previewRowState.paymentGroupId === paymentGroupId,
    [previewRowState, paymentGroupId],
  );

  const tableData = showPreviewRow ? [...data, PREVIEW_ROW] : data;

  useEffect(() => {
    if (showPreviewRow && data.length > MINIMUM_ROW_NUM_TO_SCROLL) {
      tableRef.current?.scrollTo({
        index: data.length - MINIMUM_ROW_NUM_TO_SCROLL,
      });
    }
  }, [showPreviewRow, data]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: '#EAEAEA',
            headerColor: '#00000073',
            rowExpandedBg: '#FAFAFA',
            headerSplitColor: '#A0A0A0',
          },
        },
      }}
    >
      <Table
        enableSticky={false}
        ref={tableRef}
        rowKey="id"
        className={tableStyles}
        dataSource={tableData}
        locale={{ emptyText: ' ' }}
        style={{ minWidth: 960 }}
        rowSelection={{
          renderCell: () => null,
          hideSelectAll: true,
          columnWidth: 0,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('payment-name')}
              />
            ),
            key: 'name',
            dataIndex: 'name',
            ellipsis: true,
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return (
                  <Text
                    weight="regular"
                    color="secondary"
                    text={t('preview-row-description', {
                      item: t('payment'),
                    })}
                  />
                );
              }
              return <Text text={value} maxWidth={240} />;
            },
            width: 240,
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('corporate-number')}
              />
            ),
            key: 'corporateNumber',
            dataIndex: 'corporateNumber',
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return <Text text={value} />;
            },
            width: 130,
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('amount-of-expenditures')}
              />
            ),
            key: 'totalContractAmount',
            dataIndex: 'totalContractAmount',
            align: 'right',
            width: TABLE_MONETARY_AMOUNT_WIDTH,
            render: (_, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return (
                <Text
                  text={paymentValueConverter(
                    record.totalContractAmount,
                    unit,
                    !!devtoolState?.isDebugMode,
                    record.negativeTotalContractAmountCount,
                  )}
                />
              );
            },
          },
          {},
          ...(project.data && !project.data.isLocked && editPermission.data
            ? [
                {
                  title: (
                    <PublishedProjectMutableGuard>
                      <Flex justify="end">
                        <Button
                          onClick={() => {
                            setDrawerState({
                              open: true,
                              type: 'payee-payment',
                              isEditing: false,
                              paymentGroupId,
                            });
                            setPreviewRowState({
                              type: 'payee-payment',
                              paymentGroupId,
                            });
                            setSelectedRowKeys([PREVIEW_ROW_ID]);
                          }}
                        >
                          {t('create-payment')}
                        </Button>
                      </Flex>
                    </PublishedProjectMutableGuard>
                  ),
                  key: 'actions',
                  width: Number(TABLE_ROW_MENU_WIDTH) + 20,
                  fixed: 'right' as const,
                  render: (_, record) => (
                    <PublishedProjectMutableGuard>
                      <Flex justify="end" style={{ marginRight: 8 }}>
                        <TableRowMenuCell
                          onEdit={() => {
                            setDrawerState({
                              open: true,
                              type: 'payee-payment',
                              isEditing: true,
                              paymentGroupId: record.paymentGroupId,
                              paymentId: record.id,
                            });
                          }}
                          items={[
                            {
                              key: 'delete',
                              label: t('delete'),
                              danger: true,
                              onClick: () => {
                                onClickDelete(record);
                              },
                            },
                          ]}
                        />
                      </Flex>
                    </PublishedProjectMutableGuard>
                  ),
                },
              ]
            : []),
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          expandedRowRender: (payment) => (
            <PaymentContractsTable
              data={payment.contracts}
              paymentGroupId={paymentGroupId}
              paymentId={payment.id}
              paymentType="payee"
              onClickDelete={(value) => {
                setPaymentContractForDelete(value);
              }}
            />
          ),

          defaultExpandAllRows: true,
        }}
        pagination={false}
      />
      <ConfirmModal
        open={paymentContractForDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: paymentContractForDelete?.overview ?? '',
        })}
        onOk={async () => {
          invariant(paymentContractForDelete, t('unreachable-code'));
          try {
            await deletePaymentContractAsync({
              id: paymentContractForDelete.id,
              projectId,
              paymentGroupId,
              paymentId: paymentContractForDelete.paymentId,
            });
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setPaymentContractForDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setPaymentContractForDelete(null);
        }}
        skipCheck
      />
    </ConfigProvider>
  );
};
